import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { InfoLayout } from "@/components/laneguide/InfoLayout";
import { LenderLogo } from "@/components/laneguide/LenderLogo";
import { LENDERS, ucLabel, applyUrl } from "@/lib/lenders";
import { LoanWarning } from "@/components/laneguide/LoanWarning";
import { ArrowRight } from "lucide-react";
import { StarRating } from "@/components/laneguide/StarRating";

function formatKr(n: number) {
  return new Intl.NumberFormat("sv-SE").format(n) + " kr";
}

const BY_SLUG = Object.fromEntries(
  Object.values(LENDERS).map((l) => [l.slug, l]),
);

export const Route = createFileRoute("/privatlan/$slug")({
  head: ({ params }) => {
    const l = BY_SLUG[params.slug];
    return {
      meta: [
        { title: l ? `${l.name} – Villkor, ränta & räkneexempel | Svensk Låneguide` : "Långivare | Svensk Låneguide" },
        { name: "description", content: l ? `Allt om ${l.name}: belopp ${formatKr(l.minAmount)}–${formatKr(l.maxAmount)}, ränta ${l.rate}, löptid ${l.term}. Krav, avgifter och räkneexempel.` : "Långivare hos Svensk Låneguide." },
        { property: "og:title", content: l ? `${l.name} – Villkor & räkneexempel` : "Långivare" },
        { property: "og:description", content: l?.description ?? "Långivare" },
        { property: "og:type", content: "website" },
      ],
    };
  },
  loader: ({ params }) => {
    if (!BY_SLUG[params.slug]) throw notFound();
    return { slug: params.slug };
  },
  component: LenderPage,
});

function LenderPage() {
  const { slug } = Route.useParams();
  const l = BY_SLUG[slug]!;
  const similar = Object.values(LENDERS)
    .filter((o) => o.id !== l.id && o.categories.some((c) => l.categories.includes(c)))
    .slice(0, 3);

  return (
    <InfoLayout title={`${l.name} – lån, villkor och räkneexempel`}>
      <div className="not-prose mb-6 flex flex-col items-center gap-5 rounded-md border border-border bg-card p-5 text-center sm:flex-row sm:text-left">
        <div className="flex h-24 w-40 shrink-0 items-center justify-center">
          <LenderLogo lender={l} size={160} bare className="!h-auto w-full" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-center gap-1 text-sm text-foreground/70 sm:justify-start">
            <StarRating rating={l.rating} />
            <span className="font-bold text-foreground">{l.rating}/5</span>
          </div>
          <p className="mt-2 break-words text-sm text-foreground/80">
            {l.description}
            {l.descriptionHighlight && (
              <>
                {" "}
                <span className="font-semibold text-primary underline underline-offset-2">
                  {l.descriptionHighlight}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      <h2>Om {l.name}</h2>
      <p>
        {l.name} är en {l.usesUc ? "kreditgivare som tar UC-upplysning" : "kreditgivare utan UC (använder annan kreditupplysning)"} och erbjuder lån mellan {formatKr(l.minAmount)} och {formatKr(l.maxAmount)} med löptid {l.term.toLowerCase()}. Räntespannet är {l.rate}{l.setupFee !== "0 kr" ? ` och uppläggningsavgiften är ${l.setupFee}` : " och det tas ingen uppläggningsavgift"}. Kategorier: {l.categories.join(", ")}.
      </p>

      <h2>Lånevillkor</h2>
      <ul>
        <li><b>Lånebelopp:</b> {formatKr(l.minAmount)} – {formatKr(l.maxAmount)}</li>
        <li><b>Löptid:</b> {l.term}</li>
        <li><b>Ränta:</b> {l.rate}</li>
        <li><b>Uppläggningsavgift:</b> {l.setupFee}</li>
        <li><b>Utan UC:</b> {ucLabel(l)}</li>
        <li><b>Direktutbetalning:</b> {l.directPayout ? "Ja" : "Nej"}</li>
        <li><b>Betalningsanmärkning:</b> {l.allowsRemarks ? "Accepteras" : "Accepteras ej"}</li>
        <li><b>Åldersgräns:</b> {l.minAge} år</li>
        {l.minIncome && <li><b>Min. inkomst:</b> {formatKr(l.minIncome)} / mån</li>}
      </ul>

      <h2>Krav för att ansöka</h2>
      <ul>
        <li>Minst {l.minAge} år (i regel max 80 år)</li>
        <li>Folkbokförd i Sverige med svenskt personnummer</li>
        <li>{l.requiresFixedEmployment ? "Fast anställning eller annan säker inkomst" : "Regelbunden inkomst"}{l.minIncome ? ` (minst ${formatKr(l.minIncome)}/mån)` : ""}</li>
        <li>Kreditprövning via {l.usesUc ? "UC" : "Creditsafe/Bisnode"}</li>
        {!l.allowsRemarks && <li>Ingen aktiv betalningsanmärkning</li>}
      </ul>

      <h2>Räkneexempel</h2>
      <p><em>{l.example}</em></p>
      {l.extraLink && (
        <div>
          {l.extraLink.description && (
            <p className="mb-1 text-sm leading-relaxed text-muted-foreground">{l.extraLink.description}</p>
          )}
          <p>
            <a href={l.extraLink.href} target="_blank" rel="noreferrer" className="text-primary underline">
              {l.extraLink.label}
            </a>
          </p>
        </div>
      )}

      <h2>Villkor från {l.name}</h2>
      <p>
        Lånebelopp {formatKr(l.minAmount)}–{formatKr(l.maxAmount)}. Återbetalningstid {l.term}. Ränta {l.rate}. Uppläggningsavgift {l.setupFee}. Villkoren för ditt lån baseras på ditt val av långivare och dina förutsättningar.
      </p>

      <div className="not-prose my-8 rounded-md border border-border bg-card p-6">
        <div className="font-display text-xl font-extrabold text-foreground">Ansök om lån hos {l.name}</div>
        <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <div><b>Belopp:</b> {formatKr(l.minAmount)} – {formatKr(l.maxAmount)}</div>
          <div><b>Ränta:</b> {l.rate}</div>
          <div><b>Löptid:</b> {l.term}</div>
          <div><b>Avgift:</b> {l.setupFee}</div>
        </div>
        <a href={applyUrl(l, "privatlan", "lender-hero-cta")} target="_blank" rel="noopener noreferrer sponsored" className="mt-5 inline-flex items-center gap-2 rounded-md bg-cta px-6 py-3 text-sm font-bold !text-cta-foreground !no-underline">
          Ansök nu <ArrowRight className="h-4 w-4" />
        </a>
      </div>

      {similar.length > 0 && (
        <>
          <h2>Liknande låneprogram</h2>
          <div className="not-prose grid gap-3 sm:grid-cols-3">
            {similar.map((s) => (
              <Link key={s.id} to="/privatlan/$slug" params={{ slug: s.slug }} className="rounded-md border border-border bg-card p-4 hover:border-primary/40">
                <div className="flex h-16 items-center justify-center"><LenderLogo lender={s} size={100} bare className="!h-auto w-full" /></div>
                <div className="mt-2 text-center text-sm font-bold text-foreground">{s.name}</div>
                <div className="mt-1 text-center text-xs text-muted-foreground">{s.rate}</div>
              </Link>
            ))}
          </div>
        </>
      )}

      <LoanWarning className="not-prose mt-8" />
    </InfoLayout>
  );
}
